import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@frontegg/react";
import { useMutation, useQuery } from "@apollo/client";
import { Box, Button, CircularProgress } from "@mui/material";
import { BRAND_KEYS, Celebration, DEFAULT_BRAND, toast } from "@outpave/ds";
import httpClient from "utils/httpClient";
import { useBrandingContext } from "context/BrandingContext";
import PlatformBrandingFileKey from "enums/PlatformBrandingFileKey";
import {
  GET_PLATFORM_BRANDING,
  UPDATE_PLATFORM_BRANDING,
} from "graphql/platform";
import BrandLogosPanel, {
  BrandLogoSlot,
  LOGO_ACCEPT,
} from "components/common/branding/BrandLogosPanel";
import {
  BRAND_CARD_KEYS,
  BRAND_CHART_KEYS,
  BRAND_COLOR_KEYS,
  chartChoiceOf,
  chartKeysOf,
} from "components/common/branding/BrandColorsPanel";
import BrandingPage from "components/common/branding/BrandingPage";
import PlatformCardDesign from "components/platform/branding/PlatformCardDesign";
import { useDraftTokens } from "components/common/branding/useDraftTokens";
import { cardFills } from "components/common/cards/CompanyPaymentCard";
import { useThemePreset } from "theme/themePresetStore";
import { useNavBreadcrumbs } from "hooks/useNavBreadcrumbs";
import { Inline } from "components/layout/reusableLayouts";
import ResetBrandingButton from "components/common/branding/ResetBrandingButton";
import { resolveBrandIdentity } from "theme/brandIdentity";

const LOGO_SLOTS: BrandLogoSlot[] = [
  {
    key: PlatformBrandingFileKey.Logo,
    label: "Light logo",
    helper: "LogoSizeHint",
    accept: LOGO_ACCEPT,
    surface: "light",
  },
  {
    key: PlatformBrandingFileKey.LightLogo,
    label: "Dark logo",
    helper: "LogoSizeHint",
    accept: LOGO_ACCEPT,
    surface: "dark",
  },
  {
    key: PlatformBrandingFileKey.Favicon,
    label: "Favicon",
    helper: "FaviconSizeHint",
    accept: LOGO_ACCEPT,
  },
];

const COLOR_KEYS = [
  ...BRAND_COLOR_KEYS,
  ...BRAND_CHART_KEYS,
  ...BRAND_CARD_KEYS,
];

// The Card tab edits the face and its text colour; the older art and background keys have no editor here, so Reset leaves them as stored.
const RESET_KEYS = [
  ...BRAND_COLOR_KEYS,
  ...BRAND_CHART_KEYS,
  "paymentCardFace",
  "paymentCardTextMode",
];

const sameColors = (a: Record<string, string>, b: Record<string, string>) =>
  COLOR_KEYS.every((key) => (a[key] || "") === (b[key] || ""));

const isCard = (key: string) => key === PlatformBrandingFileKey.CardImage;

const errorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error && err.message) return err.message;
  const response = (err as { response?: { data?: { message?: string } } })
    ?.response;
  return response?.data?.message;
};

type BrandingFile = { assetUrl?: string | null } | null;
interface BrandingKey {
  key: string;
  name?: string | null;
  file?: BrandingFile;
}
interface BrandingData {
  myPlatform?: {
    id: string;
    name: string;
    branding?: { keys: BrandingKey[] } | null;
  } | null;
}

const PlatformBranding: React.FC = () => {
  const { t } = useTranslation();
  const breadcrumbs = useNavBreadcrumbs(t("Branding"));
  const { user } = useAuth();
  const { data, loading, refetch } = useQuery<BrandingData>(
    GET_PLATFORM_BRANDING,
    {
      fetchPolicy: "cache-and-network",
    },
  );
  const [updateBranding, { loading: saving }] = useMutation(
    UPDATE_PLATFORM_BRANDING,
  );
  // Every slot with an operation in flight, not just the most recent: two can
  // overlap, and the first to finish must not unlock the other.
  const [uploadingKeys, setUploadingKeys] = useState<string[]>([]);
  const [celebrating, setCelebrating] = useState(false);

  const { logos: currentLogos, refetchBranding } = useBrandingContext();

  const authHeaders = useMemo(
    () =>
      user?.accessToken
        ? { Authorization: `Bearer ${user.accessToken}` }
        : undefined,
    [user?.accessToken],
  );

  const remoteKeys = useMemo(() => {
    const map: Record<string, BrandingKey> = {};
    for (const k of data?.myPlatform?.branding?.keys || []) map[k.key] = k;
    return map;
  }, [data]);

  const savedColors = useMemo(
    () =>
      Object.fromEntries(
        COLOR_KEYS.map((key) => [key, remoteKeys[key]?.name || ""]),
      ),
    [remoteKeys],
  );

  const [currentColors, setCurrentColors] =
    useState<Record<string, string>>(savedColors);
  const [colors, setColors] = useState<Record<string, string>>(savedColors);

  // A logo upload refetches the record, and that must not wipe a colour edit:
  // the draft follows the record only while it is untouched.
  useEffect(() => {
    if (sameColors(savedColors, currentColors)) return;
    const pristine = sameColors(colors, currentColors);
    setCurrentColors(savedColors);
    if (pristine) setColors(savedColors);
  }, [savedColors, currentColors, colors]);

  const colorsAreDirty = !sameColors(colors, currentColors);

  const isResetState = RESET_KEYS.every((key) => !colors[key]);

  // Staged, committed by Save. Each logo slot has its own Remove.
  const handleReset = () => {
    setColors((prev) => ({
      ...prev,
      ...Object.fromEntries(RESET_KEYS.map((key) => [key, ""])),
    }));
  };

  const setColor = (key: string, hex: string) =>
    setColors((prev) => ({ ...prev, [key]: hex }));

  const celebrationColors = useMemo(() => {
    const identity =
      resolveBrandIdentity(colors, null) ?? BRAND_KEYS[DEFAULT_BRAND];
    return [identity.primary, identity.secondary, identity.tertiary];
  }, [colors]);

  const handleSave = async () => {
    // Only what changed, and "" for a cleared key: the server keeps everything else as stored.
    const keys = COLOR_KEYS.filter(
      (key) => (colors[key] || "") !== (currentColors[key] || ""),
    ).map((key) => ({ key, name: colors[key]?.trim() || "" }));
    const navBackground = keys.find((change) => change.key === "navBackground");
    if (navBackground) {
      keys.push({ key: "pageBackground", name: navBackground.name });
    }
    try {
      await updateBranding({ variables: { input: { keys } } });
    } catch (err: unknown) {
      toast.error(errorMessage(err) || t("CouldNotSaveBranding"));
      return;
    }
    // Saved either way; only the live look could not follow.
    if (!(await refetchBranding())) {
      toast.warning(t("SavedReloadToSee"));
      return;
    }
    const name = data?.myPlatform?.name?.trim();
    toast.success(
      name
        ? t("BrandingSavedNewLook", { name })
        : t("BrandingSavedNewLookGeneric"),
    );
    setCelebrating(true);
  };

  /**
   * Rereads the record and the live branding after a file change. A failure
   * here is not a failed change, which already landed, so it says to reload.
   */
  const refreshAfterFileChange = async (): Promise<boolean> => {
    let fresh = false;
    try {
      await refetch();
      // The context swallows its own request errors, so it answers rather than throws.
      fresh = await refetchBranding();
    } catch {
      fresh = false;
    }
    if (!fresh) toast.warning(t("SavedReloadToSee"));
    return fresh;
  };

  /** Resolves true once the file is stored, even when the refresh after it fails. */
  const handleUpload = async (key: string, file: File): Promise<boolean> => {
    const isSvg =
      file.type === "image/svg+xml" || file.name.toLowerCase().endsWith(".svg");
    if (!isSvg) {
      toast.error(t("Only SVG files are allowed for platform branding"));
      return false;
    }
    setUploadingKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
    try {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("key", key);
        await httpClient.post("/api/v1/platform/branding/file", formData, {
          headers: authHeaders,
        });
      } catch (err: unknown) {
        toast.error(errorMessage(err) || t("LogoUploadFailed"));
        return false;
      }
      if (await refreshAfterFileChange()) {
        toast.success(t(isCard(key) ? "CardArtworkUploaded" : "LogoUploaded"));
      }
      return true;
    } finally {
      setUploadingKeys((keys) => keys.filter((k) => k !== key));
    }
  };

  const handleRemove = async (key: string) => {
    setUploadingKeys((keys) => (keys.includes(key) ? keys : [...keys, key]));
    try {
      try {
        await httpClient.delete(`/api/v1/platform/branding/file/${key}`, {
          headers: authHeaders,
        });
      } catch (err: unknown) {
        toast.error(
          errorMessage(err) ||
            t(isCard(key) ? "CouldNotRemoveCardArtwork" : "CouldNotRemoveLogo"),
        );
        return;
      }
      if (await refreshAfterFileChange()) {
        toast.success(t(isCard(key) ? "CardArtworkRemoved" : "LogoRemoved"));
      }
    } finally {
      setUploadingKeys((keys) => keys.filter((k) => k !== key));
    }
  };

  const cardUrl =
    remoteKeys[PlatformBrandingFileKey.CardImage]?.file?.assetUrl || undefined;

  // The card face never repaints with the scheme, so either scheme's fills serve.
  const themePreset = useThemePreset();
  const draftTokens = useDraftTokens(colors, null, "dark", themePreset);
  const fills = cardFills(draftTokens);

  const currentUrls: Record<string, string | undefined> = {
    [PlatformBrandingFileKey.Logo]:
      remoteKeys[PlatformBrandingFileKey.Logo]?.file?.assetUrl || undefined,
    [PlatformBrandingFileKey.LightLogo]:
      remoteKeys[PlatformBrandingFileKey.LightLogo]?.file?.assetUrl ||
      undefined,
    [PlatformBrandingFileKey.Favicon]:
      remoteKeys[PlatformBrandingFileKey.Favicon]?.file?.assetUrl || undefined,
  };

  if (loading && !data) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <BrandingPage
      breadcrumbs={breadcrumbs}
      title={t("Branding")}
      subtitle={t("BrandingPageDescription")}
      secondaryAction={
        <Inline gap="var(--space-3)" wrap={false}>
          <ResetBrandingButton
            onReset={handleReset}
            disabled={saving || isResetState}
            message={t("ResetBrandingPlatform")}
          />
          {/* Back to the last save, where Reset goes to the defaults. */}
          <Button
            variant="default-outlined"
            onClick={() => setColors(currentColors)}
            disabled={saving || !colorsAreDirty}
          >
            {t("Discard changes")}
          </Button>
        </Inline>
      }
      primaryAction={
        <Button
          variant="primary-filled"
          onClick={handleSave}
          disabled={saving || !colorsAreDirty}
          startIcon={
            saving ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {t("Save changes")}
        </Button>
      }
      logos={
        <BrandLogosPanel
          slots={LOGO_SLOTS}
          currentUrls={currentUrls}
          // A platform inherits only the product's own marks, which is not a
          // logo of its own, so an empty slot offers the upload.
          fallbackUrls={{}}
          uploadingKeys={uploadingKeys}
          onUpload={async (key, file) => {
            await handleUpload(key, file);
          }}
          onRemove={handleRemove}
        />
      }
      extraTabs={[
        {
          id: "card",
          label: "Card",
          fullWidth: true,
          content: (
            <PlatformCardDesign
              url={cardUrl}
              uploading={uploadingKeys.includes(
                PlatformBrandingFileKey.CardImage,
              )}
              onUpload={(file) =>
                handleUpload(PlatformBrandingFileKey.CardImage, file)
              }
              onRemove={() => handleRemove(PlatformBrandingFileKey.CardImage)}
              face={colors.paymentCardFace || ""}
              textMode={colors.paymentCardTextMode || ""}
              art={colors.paymentCardArt || ""}
              background={colors.paymentCardBackground || ""}
              fills={fills}
              onFaceChange={(face) => setColor("paymentCardFace", face)}
              onTextModeChange={(mode) => setColor("paymentCardTextMode", mode)}
            />
          ),
        },
      ]}
      colors={colors}
      onColorChange={setColor}
      chart={chartChoiceOf(colors)}
      onChartChange={(next) =>
        setColors((prev) => ({
          ...prev,
          ...chartKeysOf(next, chartChoiceOf(prev)),
        }))
      }
      previewLogoUrl={
        currentUrls[PlatformBrandingFileKey.Logo] || currentLogos?.logo
      }
      previewLightLogoUrl={
        currentUrls[PlatformBrandingFileKey.LightLogo] ||
        currentLogos?.lightLogo
      }
    >
      <Celebration
        active={celebrating}
        onDone={() => setCelebrating(false)}
        colors={celebrationColors}
      />
    </BrandingPage>
  );
};

export default PlatformBranding;
